import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import * as lager from "./dokumentlager";

/**
 * Shotlista i feltappen.
 *
 * SETTES OPP PAA NETTFLATA, HUKES AV HER. A-foto, redaktoer og produsent
 * legger inn scenene og bildene; i felt er handlingen én — at bildet er
 * tatt.
 *
 * OFFLINE FOERST, som alt annet. `lesLokalt` svarer fra disk uten aa roere
 * nettet, `synkroniser` kjoeres etterpaa, og en hake gaar i utboksen til
 * dekningen kommer.
 *
 * REFERANSEBILDET LASTES NED. Det ligger i OPFS som dokumentene, og vises
 * derfra — ellers ville bildet man trenger naar man rigger vaert det ene som
 * krevde nett. Og det foelger samme regel som dokumentene: en blob-URL som
 * lever i denne fana, ingen delingsknapp, ingen vei ut.
 */

const BOETTE = "referansebilder";

export interface Scene {
  readonly id: string;
  readonly navn: string;
  readonly sortOrder: number;
}

export interface Shot {
  readonly id: string;
  readonly sceneId: string;
  readonly tekst: string;
  readonly notat: string;
  readonly bildeSti: string | null;
  readonly gjort: boolean;
  readonly sortOrder: number;
}

export interface Shotliste {
  readonly scener: readonly Scene[];
  readonly shots: readonly Shot[];
}

export const TOM: Shotliste = { scener: [], shots: [] };

/** Filnavnet et referansebilde faar i OPFS. */
const filnavn = (sti: string): string => `ref-${sti.replace(/[^\w.-]/g, "_")}`;

export async function lesLokalt(
  produksjonId: string, opptaksdag: string,
): Promise<Shotliste> {
  await db.openDb(produksjonId);
  return db.lastShotliste(produksjonId, opptaksdag);
}

/**
 * Henter fra Supabase og skriver over det lokale.
 *
 * ET SHOT SOM VENTER I UTBOKSEN ROERES IKKE. Synken bytter hele settet, og
 * uten dette ville den overskrevet en hake som ennaa ikke hadde kommet fram
 * — brukeren ville sett haken forsvinne av seg selv.
 */
export async function synkroniser(
  produksjonId: string, opptaksdag: string,
): Promise<Shotliste | null> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return null;
  const s = await supabase
    .from("shot_scene")
    .select("id, name, sort_order")
    .eq("production_id", produksjonId)
    .eq("shoot_day", opptaksdag)
    .order("sort_order", { ascending: true });
  if (s.error !== null) return null;
  const scener: Scene[] = (s.data ?? []).map((r) => ({
    id: String(r.id), navn: String(r.name), sortOrder: Number(r.sort_order),
  }));

  let shots: Shot[] = [];
  if (scener.length > 0) {
    const b = await supabase
      .from("shot")
      .select("id, scene_id, text, note, image_path, done, sort_order")
      .in("scene_id", scener.map((x) => x.id))
      .order("sort_order", { ascending: true });
    if (b.error !== null) return null;
    shots = (b.data ?? []).map((r) => ({
      id: String(r.id),
      sceneId: String(r.scene_id),
      tekst: String(r.text),
      notat: String(r.note),
      bildeSti: r.image_path === null ? null : String(r.image_path),
      gjort: r.done === true,
      sortOrder: Number(r.sort_order),
    }));
  }

  await db.openDb(produksjonId);
  const venter = new Map(
    (await db.utboksShots(produksjonId)).map((r) => [r.shotId, r.gjort]),
  );
  const flettet = venter.size === 0
    ? shots
    : shots.map((b) => {
      const egen = venter.get(b.id);
      return egen === undefined ? b : { ...b, gjort: egen };
    });

  await db.erstattShotliste(produksjonId, opptaksdag, scener, flettet);
  return { scener, shots: flettet };
}

/**
 * Laster ned referansebildene som mangler. Returnerer hvor mange som kom.
 *
 * KJOERES ETTER lista, ikke foran: bildene skal ikke holde igjen det man
 * leser. Men den som kaller maa tegne paa nytt naar den er ferdig — ellers
 * har flata alt sett etter en fil som ikke laa der, og ser aldri igjen. Det
 * var nettopp det som gjorde at bildet ikke dukket opp i appen.
 *
 * Et bilde som alt ligger der hentes ikke paa nytt. Stien inneholder
 * shot-id-en, saa den endrer seg naar bildet byttes.
 */
export async function hentBilder(shots: readonly Shot[]): Promise<number> {
  if (supabase === null) return 0;
  let nye = 0;
  for (const b of shots) {
    const sti = b.bildeSti;
    if (sti === null) continue;
    if (await lager.les(filnavn(sti)) !== null) continue;
    const { data, error } = await supabase.storage.from(BOETTE).download(sti);
    if (error !== null || data === null) continue;
    if (await lager.lagre(filnavn(sti), data)) nye += 1;
  }
  return nye;
}

/** Blob-URL til et nedlastet referansebilde, eller null. */
export async function bilde(sti: string): Promise<string | null> {
  return lager.les(filnavn(sti));
}

/**
 * Huker av et shot.
 *
 * LOKALT FOERST, ALLTID. Man huker av i en kjeller, og det skal staa der med
 * én gang — ikke vente paa dekning.
 */
export async function settGjort(
  produksjonId: string, shotId: string, gjort: boolean,
): Promise<void> {
  await db.openDb(produksjonId);
  await db.settShotGjort(produksjonId, shotId, gjort);
}

/**
 * Toemmer utboksen. Returnerer hvor mange haker som gikk opp.
 *
 * Det som sendes er alltid det som STAAR lokalt, saa ti raske trykk blir én
 * sending per shot.
 */
export async function send(produksjonId: string): Promise<number> {
  if (supabase === null || !navigator.onLine || !erEkteProduksjon(produksjonId)) return 0;
  await db.openDb(produksjonId);
  const koe = await db.utboksShots(produksjonId);
  let sendt = 0;
  for (const rad of koe) {
    const { error } = await supabase
      .from("shot")
      .update({ done: rad.gjort })
      .eq("id", rad.shotId);
    // Feiler én, staar den i koeen. Vi gaar videre: et annet shot skal ikke
    // bli staaende fordi dette ikke kom fram.
    if (error === null) {
      await db.utboksShotKvitter(rad.shotId);
      sendt += 1;
    }
  }
  return sendt;
}
